import React, { useContext, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Loader } from "@/UI"
import Image from "next/image"
import SettingSection from "../shared/SettingSection"
import EmptySettingBox from "../shared/EmptySettingBox.container"
import WebhooksAddEditModal from "./components/WebhooksAddEditModal"
import { Breadcrumb } from "antd"
import WebhooksLogTable from "./components/WebhooksLogTable"
import { getWebhooks, patchWebhook, postWebhook, deleteWebhook } from "@/api/webhooks"
import SettingContentWrapper from "../shared/SettingContentWrapper"
import SandboxDisclaimer from "./components/SandboxDisclaimer"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { IWebhook } from "@/types/webhooks.types"
import WebhooksTable from "./components/WebhooksTable"
import { userAtom } from "@/atoms/userAtom"
import { SandboxContext } from "@/layout/Nav/SandboxSwitcher"
import { useRecoilValue } from "recoil"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface WebhooksProps {}

const Webhooks: React.FC<WebhooksProps> = () => {
  const { t } = useTranslation()
  const [viewDetailWebhook, setViewDetailWebhook] = React.useState<IWebhook | null>(null)
  const sandbox = useContext(SandboxContext)
  const user = useRecoilValue(userAtom)
  const [webhooks, setWebhooks] = React.useState<IWebhook[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [showActionButton, setShowActionButton] = React.useState(false)

  const fetchWebhooks = async () => {
    setIsLoading(true)
    try {
      const { data } = await getWebhooks()
      setWebhooks(data)
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const addWebhook = async (values: IWebhook): Promise<Error | null> => {
    try {
      await postWebhook(values)
      await fetchWebhooks()
      return null
    } catch (error) {
      throw new Error((error as any).message || "Something went wrong when adding webhook")
    }
  }

  const updateWebhook = async (values: IWebhook): Promise<Error | null> => {
    try {
      if (!values.id) {
        return new Error("id is required")
      }
      await patchWebhook(values.id, {
        url: values.url,
      })
      await fetchWebhooks()
      return null
    } catch (error) {
      throw new Error((error as any).message || "Something went wrong when updating webhook")
    }
  }

  const removeWebhook = async (id: string): Promise<Error | null> => {
    try {
      await deleteWebhook(id)
      await fetchWebhooks()
      return null
    } catch (error) {
      throw new Error((error as any).message || "Something went wrong when deleting webhook")
    }
  }

  useEffect(() => {
    fetchWebhooks()
  }, [sandbox.enabled, user.org.id])

  const content = useMemo(() => {
    switch (true) {
      case isLoading:
        setShowActionButton(false)
        return <Loader fontSize={48} />
      case !webhooks.length:
        setShowActionButton(false)
        return (
          <EmptySettingBox
            id="empty-webhooks"
            icon={
              <Image
                src={"/assets/images/developers/emptyWebhookIllu.svg"}
                alt="logo"
                width={80}
                height={80}
              />
            }
            description={t("developers.Webhooks.noWebhooksYet")}
            button={
              <WebhooksAddEditModal
                title={t("developers.Webhooks.addWebhook")}
                button={
                  <KosmoButton id="empty-webhooks-add-button" type="primary">
                    <div className="flex items-center gap-x-2">
                      <FontAwesomeIcon icon={faPlus} color="#FFF" />
                      <p>{t("developers.Webhooks.addWebhook")}</p>
                    </div>
                  </KosmoButton>
                }
                handleSubmit={addWebhook}
                webhooks={webhooks}
              />
            }
          />
        )
      case !!viewDetailWebhook:
        setShowActionButton(false)
        return <WebhooksLogTable viewDetailWebhook={viewDetailWebhook} />
      default:
        setShowActionButton(true)
        return (
          <WebhooksTable
            webhooks={webhooks}
            setViewDetailWebhook={setViewDetailWebhook}
            updateWebhook={updateWebhook}
            removeWebhook={removeWebhook}
          />
        )
    }
  }, [isLoading, webhooks, viewDetailWebhook])

  return (
    <SettingSection
      title={t("developers.webhooks")}
      actionButton={
        showActionButton && (
          <WebhooksAddEditModal
            title={t("developers.Webhooks.addWebhook")}
            button={
              <KosmoButton type="primary">
                <div className="flex items-center gap-x-2">
                  <FontAwesomeIcon icon={faPlus} color="#FFF" />
                  <p>{t("developers.Webhooks.addWebhook")}</p>
                </div>
              </KosmoButton>
            }
            handleSubmit={addWebhook}
            webhooks={webhooks}
          />
        )
      }
      {...(viewDetailWebhook
        ? {
            breadcrumb: (
              <Breadcrumb separator=">">
                <Breadcrumb.Item
                  href=""
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setViewDetailWebhook(null)
                  }}
                >
                  {t("developers.webhooks")}
                </Breadcrumb.Item>
                <Breadcrumb.Item>{viewDetailWebhook?.url}</Breadcrumb.Item>
              </Breadcrumb>
            ),
          }
        : {})}
    >
      {!viewDetailWebhook && <SandboxDisclaimer withToggle />}
      <SettingContentWrapper>{content}</SettingContentWrapper>
    </SettingSection>
  )
}

export default Webhooks
